import moment from 'moment'
import { nextVisit, procedureGroups } from 'app/components/action-center/treatmentDisplay'
import type { TreatmentRow } from 'app/components/action-center/useTreatmentBoard'
import type { TreatmentColumnId } from 'app/types/treatment'
import type { CategoryResolver } from 'lib/cdtCodeDefaults'
import { formatCurrency } from 'lib/formatCurrency'

// CSV dates are a fixed YYYY-MM-DD rather than the on-screen `shortDate`: a
// spreadsheet needs to sort and filter the column, and `shortDate` drops the
// year for the current one, which a saved export can't fall back on later.
function csvDate(value: string | null | undefined): string {
  if (!value) return ``
  const date = moment(value)
  return date.isValid() ? date.format(`YYYY-MM-DD`) : ``
}

type LocationNames = Map<string, { name: string }>

// A switch over every column id rather than a lookup table: a column added to the table
// without an export here fails to compile at the `never` check.
function fieldValue(
  id: TreatmentColumnId,
  row: TreatmentRow,
  categoryOf: CategoryResolver,
  locationNames?: LocationNames,
): string {
  switch (id) {
    case `Patient`:
      return row.name
    case `Unscheduled`:
      return formatCurrency(row.value)
    case `Planned`:
      return csvDate(row.presented)
    case `Stage`:
      return row.stage
    case `Last visit`: {
      const on = csvDate(row.lastVisitOn)
      if (!on) return ``
      return row.lastVisitBroken ? `Broken ${on}` : on
    }
    case `Next visit`: {
      const visit = nextVisit(row, categoryOf)
      if (!visit) return ``
      const on = csvDate(visit.on)
      return on ? `${visit.word} ${on}` : visit.word
    }
    case `Treatment`:
      return procedureGroups(row, categoryOf)
        .map((group) => `${group.label} (${formatCurrency(group.fee)})`)
        .join(`; `)
    case `Phone`:
      return (row.cellPhone ?? ``).replace(/\D/g, ``)
    case `Broken`:
      return formatCurrency(row.brokenValue)
    case `Scheduled`:
      return formatCurrency(row.scheduledValue)
    case `Completed`:
      return formatCurrency(row.completedValue)
    case `Lifetime production`:
      return formatCurrency(row.historyValue)
    case `Location`:
      return (row.locationId && locationNames?.get(row.locationId)?.name) || ``
    default: {
      const unhandled: never = id
      return unhandled
    }
  }
}

// Keyed by the union, so an id missing here is a compile error too.
const COLUMN_IDS: Readonly<Record<TreatmentColumnId, true>> = {
  Patient: true,
  Unscheduled: true,
  Planned: true,
  Stage: true,
  'Last visit': true,
  'Next visit': true,
  Treatment: true,
  Phone: true,
  Broken: true,
  Scheduled: true,
  Completed: true,
  'Lifetime production': true,
  Location: true,
}

const isColumnId = (id: string): id is TreatmentColumnId => Object.prototype.hasOwnProperty.call(COLUMN_IDS, id)

const NEEDS_QUOTES = /[",\r\n]/

function csvField(value: string): string {
  return NEEDS_QUOTES.test(value) ? `"${value.replace(/"/g, `""`)}"` : value
}

/** Headers that differ from their column id, which saved column settings keep pinned. */
const HEADER: Partial<Record<TreatmentColumnId, string>> = { Planned: `Presented` }

/** The rows a page has loaded, as CSV in the given column order; ids with no field are skipped. */
export function exportTreatmentRows(
  rows: TreatmentRow[],
  columnIds: string[],
  categoryOf: CategoryResolver,
  locationNames?: LocationNames,
): string {
  const ids = columnIds.filter(isColumnId)
  const lines = [
    ids.map((id) => HEADER[id] ?? id),
    ...rows.map((row) => ids.map((id) => fieldValue(id, row, categoryOf, locationNames))),
  ]
  return lines.map((line) => line.map(csvField).join(`,`)).join(`\n`)
}
